/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import { useMemo } from "react";
import ArrowDown from "../../icons/ArrowDown.svg?react";
import ArrowUp from "../../icons/ArrowUp.svg?react";
import ZoomIn from "../../icons/ZoomIn.svg?react";
import { PROP_KEYS } from "../../shared/operations/download/download-operations";
import { t } from "../../translations/i18n";

const SKELETON_ROW_COUNT = 10 as const;

export const SmallerCellStyle = css`
  max-width: 40px;
`;

const EmptyStateStyle = css`
  margin-top: 16px;
`;

interface IStep {
  vtp: string;
  values: Array<number>;
}

interface IProps {
  steps: Array<IStep> | null;
  zoomToElement: (vtp: string, length: number, rowIndex: number) => void;
  isFetching: boolean;
  geometryPropNames: Array<string>;
  queryPage: number;
  stepsNumber: number;
  onChangePage: (newPage: number) => void;
  ordering: string;
  setOrder: (ord: string) => void;
  highlightedRowIndex: number | null;
}

interface IBodyProps extends IProps {
  page: number;
  rowsPerPage: number;
}

const Body = ({
  steps,
  zoomToElement,
  isFetching,
  geometryPropNames,
  queryPage,
  highlightedRowIndex,
}: IBodyProps) => {
  if (!steps && !isFetching) {
    return null;
  }

  if (steps && steps.length === 0 && !isFetching) {
    return <div css={EmptyStateStyle}>{t("NO_RESULTS_QUERY")}</div>;
  }

  // Find index of the value that holds the segment length:
  const mmgLengthIndex = geometryPropNames.indexOf(PROP_KEYS.MMG_LENGTH);

  // Find index of the queried value (segment length is appended to the query when is is not the query parameter itself)
  const standardNames = [PROP_KEYS.MMG_LENGTH];

  const valueIndex =
    geometryPropNames.length < 2
      ? mmgLengthIndex
      : geometryPropNames.findIndex(
          (name: string) => !standardNames.includes(name),
        );

  return isFetching ? (
    <>
      {[...Array(SKELETON_ROW_COUNT)].map((_unused, index) => (
        <TableRow key={index}>
          <TableCell css={SmallerCellStyle} align="center">
            <Skeleton />
          </TableCell>
          <TableCell align="left">
            <Skeleton />
          </TableCell>
          <TableCell css={SmallerCellStyle} align="right">
            <Skeleton />
          </TableCell>
        </TableRow>
      ))}
    </>
  ) : (
    <>
      {steps.length > 0 &&
        valueIndex !== -1 &&
        steps.map((step, index) => (
          <TableRow key={index} selected={index === highlightedRowIndex}>
            <TableCell
              component="th"
              scope="row"
              css={SmallerCellStyle}
              align="center"
            >
              {`${queryPage * 10 + (index + 1)}`}
            </TableCell>
            <TableCell align="left">{step.values[valueIndex]}</TableCell>
            <TableCell
              align="right"
              css={SmallerCellStyle}
              style={{ cursor: "pointer" }}
            >
              <ZoomIn
                onClick={() => {
                  const values = step.values;
                  zoomToElement(step.vtp, values[mmgLengthIndex], index);
                }}
              />
            </TableCell>
          </TableRow>
        ))}
    </>
  );
};

export const MmgConnectedGeometryQueryElementTable = (props: IProps) => {
  const {
    steps,
    setOrder,
    ordering,
    isFetching,
    queryPage,
    stepsNumber,
    onChangePage,
  } = props;

  const lastPage = useMemo(() => Math.round(stepsNumber / 10), [stepsNumber]);

  const handleOrderClick = () => {
    const newOrderClicked =
      ordering === "Ascending" ? "Descending" : "Ascending";
    setOrder(newOrderClicked);
  };

  if (!steps && !isFetching) {
    return null;
  }

  if (steps && steps.length === 0 && !isFetching) {
    return <div css={EmptyStateStyle}>{t("NO_RESULTS_QUERY")}</div>;
  }

  const Head = () => {
    if (!steps) {
      return null;
    }

    return (
      <TableRow>
        <TableCell onClick={handleOrderClick}>
          {ordering === "Ascending" ? <ArrowUp /> : <ArrowDown />}
        </TableCell>
        <TableCell>{t("PROP_MMG_LENGTH")}</TableCell>
        <TableCell>{t("ZOOM_IN")}</TableCell>
      </TableRow>
    );
  };

  const handleChangePage = (_event, newPage) => {
    onChangePage(newPage);
  };

  return (
    <>
      <Table aria-label="simple table">
        <TableHead>
          <Head />
        </TableHead>
        <TableBody>
          <Body {...props} page={queryPage} rowsPerPage={5} />
        </TableBody>
      </Table>

      <div style={{ maxWidth: "300px" }}>
        <TablePagination
          component="span"
          count={stepsNumber}
          rowsPerPageOptions={[]}
          rowsPerPage={10}
          page={queryPage}
          onPageChange={handleChangePage}
          slotProps={{
            actions: {
              previousButton: { disabled: queryPage === 0 },
              nextButton: { disabled: queryPage === lastPage },
            },
          }}
        />
      </div>
    </>
  );
};
